import React from "react";
import ExpandableNoteText from "../../PatientsIP/ProgressMonitoring/ExpandableNoteText";

const SECTION_TITLES = {
  billing_information: "Billing Information",
  patient_information: "Patient Information",
  admission_information: "Admission Information",
  billing_revenue_information: "Billing Revenue Information",
  payments_information: "Payments Information",
};

const NUMERIC_SECTIONS = new Set([
  "billing_revenue_information",
  "payments_information",
]);

const CENTER_BODY_FIELDS = new Set(["sl_no", "gender", "age", "total_bed_days"]);
const EXPANDABLE_FIELDS = new Set([
  "address",
  "type",
  "investigate",
  "other_services_revenue",
]);
const BILLING_REVENUE_TEXT_FIELDS = new Set([
  "investigate",
  "other_services_revenue",
]);
const COMPACT_TEXT_FIELDS = new Set([
  "sl_no",
  "gender",
  "age",
  "bill_date",
  "bill_no",
  "patient_id",
  "ip_id",
  "admission_date",
  "discharge_date",
  "total_bed_days",
  "contact",
  "unit_name",
  "organization_name",
  "full_name",
]);

const FIELD_LABELS = {
  sl_no: "Sl. No.",
  organization_name: "Organization Name",
  unit_name: "Unit Name",
  bill_date: "Bill Date",
  bill_no: "Bill No",
  patient_id: "Patient ID",
  full_name: "Name",
  address: "Address",
  contact: "Contact",
  gender: "Gender",
  age: "Age",
  ip_id: "IP ID",
  type: "Type",
  admission_date: "Admission Date",
  discharge_date: "Discharge Date",
  total_bed_days: "Bed Days",
  room_rent_revenue: "Room Rent Revenue",
  investigate: "Investigate",
  other_services_revenue: "Other Services Revenue",
  gross_revenue: "Gross Revenue",
  discount: "Discount",
  net_revenue: "Net Revenue",
  upi: "UPI",
  cash: "Cash",
  card: "Card",
  neft: "NEFT",
  imps: "IMPS",
  rtgs: "RTGS",
  others: "Others",
  total: "Total",
  net_due: "Net Due",
};

const amountCell = (value) =>
  value == null || value === "" ? "-" : value;

const shouldUseExpandable = (section, field, value) => {
  if (EXPANDABLE_FIELDS.has(field)) return true;
  if (
    section === "admission_information" &&
    !COMPACT_TEXT_FIELDS.has(field)
  ) {
    return true;
  }
  if (section === "billing_revenue_information") {
    if (BILLING_REVENUE_TEXT_FIELDS.has(field)) return true;
    if (value != null && value !== "" && Number.isNaN(Number(value))) {
      return true;
    }
  }
  return false;
};

const isNumericField = (section, field) => {
  if (!NUMERIC_SECTIONS.has(section)) return false;
  if (BILLING_REVENUE_TEXT_FIELDS.has(field)) return false;
  return true;
};

const textCell = (section, field) => (value) => {
  if (value == null || value === "") return "";
  const text = String(value);
  if (shouldUseExpandable(section, field, value)) {
    return <ExpandableNoteText text={text} rows={2} />;
  }
  return text;
};

const billingRevenueCell = (field) => (value) => {
  if (value == null || value === "") return "-";
  const text = String(value);
  if (shouldUseExpandable("billing_revenue_information", field, value)) {
    return <ExpandableNoteText text={text} rows={2} />;
  }
  return amountCell(value);
};

const formatFieldLabel = (field) => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return field
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getColumnWidth = (section, field) => {
  if (field === "sl_no") return 72;
  if (field === "organization_name") return 180;
  if (field === "unit_name") return 140;
  if (field === "address") return 220;
  if (field === "type") return 220;
  if (field === "investigate") return 240;
  if (field === "other_services_revenue") return 200;
  if (field === "room_rent_revenue") return 170;
  if (section === "billing_revenue_information") {
    if (field === "gross_revenue" || field === "net_revenue") return 145;
    return 130;
  }
  if (section === "payments_information") return 115;
  if (field === "full_name") return 160;
  if (field === "patient_id") return 130;
  if (field === "bill_no") return 110;
  if (field === "contact") return 130;
  if (field === "ip_id") return 110;
  if (field.includes("date")) return 140;
  if (section === "admission_information" && !COMPACT_TEXT_FIELDS.has(field)) {
    return 220;
  }
  return 140;
};

export const getTotalTableScrollWidth = (columnConfig = {}) => {
  let total = 0;
  Object.entries(columnConfig).forEach(([section, fields = []]) => {
    fields.forEach((field) => {
      total += getColumnWidth(section, field);
    });
  });
  return Math.max(total, 900);
};

export const flattenDischargeResults = (results = []) =>
  results.map((row, index) => {
    const billing = row.billing_information || {};
    const patient = row.patient_information || {};
    const admission = row.admission_information || {};
    const revenue = row.billing_revenue_information || {};
    const payments = row.payments_information || {};

    return {
      id:
        `${admission.ip_id || ""}-${billing.bill_no || ""}` ||
        `row-${index}`,
      ...billing,
      ...patient,
      ...admission,
      ...revenue,
      ...payments,
    };
  });

export const getColumnMeta = (columnConfig = {}) => {
  const numericFields = [];
  let totalColumns = 0;

  Object.entries(columnConfig).forEach(([section, fields = []]) => {
    fields.forEach((field) => {
      totalColumns += 1;
      if (isNumericField(section, field)) {
        numericFields.push(field);
      }
    });
  });

  return {
    numericFields,
    textColsAfterSlNo: totalColumns - numericFields.length - 1,
  };
};

export const buildFinalDischargeColumns = (columnConfig = {}) =>
  Object.entries(columnConfig).map(([section, fields = []]) => ({
    title: SECTION_TITLES[section] || formatFieldLabel(section),
    children: fields.map((field) => {
      const isNumeric = isNumericField(section, field);
      const columnWidth = getColumnWidth(section, field);
      const column = {
        title: formatFieldLabel(field),
        dataIndex: field,
        key: `${section}-${field}`,
        width: columnWidth,
        minWidth: columnWidth,
        ellipsis: false,
        onHeaderCell: () => ({ style: { textAlign: "left" } }),
        render:
          section === "billing_revenue_information"
            ? billingRevenueCell(field)
            : isNumeric
              ? amountCell
              : textCell(section, field),
      };

      if (CENTER_BODY_FIELDS.has(field)) {
        column.align = "center";
      } else if (isNumeric) {
        column.align = "right";
      } else {
        column.align = "left";
      }

      return column;
    }),
  }));

export const calculateTotals = (rows = [], numericFields = []) =>
  numericFields.reduce((acc, field) => {
    acc[field] = rows.reduce(
      (sum, row) => sum + (Number(row?.[field]) || 0),
      0
    );
    return acc;
  }, {});
